import React, { useMemo, useState } from 'react'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import {
    calculateEthAmountFromUsd,
    calculateUsdcAmountFromCents,
    useEthPrice,
} from '@components/Web3/useEthPrice'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { isDMChannelStreamId } from '@towns-protocol/sdk'
import { Box, Button, Stack, Text } from '@ui'
import {
    // We need useAbstractAccountAddress in order to grab the tip recipient's abstract account address so we can send the tip there!!
    // eslint-disable-next-line
    useAbstractAccountAddress,
    useMyAbstractAccountAddress,
} from 'hooks/useAbstractAccountAddress'
import { useBalance } from 'hooks/useBalance'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { useUSDCAllowance } from 'hooks/useUSDCAllowance'
import { useUSDCBalance } from 'hooks/useUsdcBalance'
import { GetSigner, PrimaryWalletNotConnectedButton, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    Address,
    BlockchainTransactionType,
    ETH_ADDRESS,
    LookupUser,
    SpaceAddressFromSpaceId,
    TipParams,
    useChannelData,
    useContractSpaceInfoWithoutClient,
    useIsTransactionPending,
    useMyUserId,
    usePrimaryWallet,
    useTipTransaction,
    useTokenIdOfOwner,
} from 'use-towns-client'
import { env } from 'utils'

import { InsufficientEOAAllowance } from './InsufficientEOAAllowance'
import { InsufficientTipBalance } from './InsufficientTipBalance'
import { trackPostedTip, trackPostedTipFailed } from './tipAnalytics'
import { TipOption } from './types'

const IS_ETH_MODE = env.VITE_TIPS_IN_ETH

export function TipConfirm(props: {
    tipValue: TipOption | undefined
    setTipValue: (tipValue: TipOption | undefined) => void
    messageOwner: LookupUser
    eventId: string
    onTip?: (tip: TipParams) => void
    onInsufficientBalance?: () => void
    onCancel?: () => void
    onTipError?: (error: Error) => void
}) {
    const {
        tipValue,
        setTipValue,
        messageOwner,
        eventId,
        onTip,
        onInsufficientBalance,
        onCancel,
        onTipError,
    } = props

    const tipPending = useIsTransactionPending(BlockchainTransactionType.Tip)

    const {
        data: price,
        isLoading: isLoadingPrice,
        isError: isErrorPrice,
        isFallbackMode,
    } = useEthPrice({
        enabled: !IS_ETH_MODE,
    })
    const spaceId = useSpaceIdFromPathname()
    const channelData = useChannelData()
    const channelId = channelData?.channelId
    const isDm = !!channelId && isDMChannelStreamId(channelId)
    const {
        data: tokenId,
        isLoading: isLoadingTokenId,
        isError: isErrorTokenId,
    } = useTokenIdOfOwner(spaceId, messageOwner.userId)

    const { data: messageOwnerAbstractAccount } = useAbstractAccountAddress({
        rootKeyAddress: messageOwner.userId as Address,
    })

    const { data: myAbstractAccount } = useMyAbstractAccountAddress()
    const userId = useMyUserId()
    const { data: primaryWallet } = usePrimaryWallet({ rootWallet: userId })
    const { enableUsdc } = useFeatureFlags()
    const { address: usdcAddress } = useUSDCMetadata()

    const walletToTransactWith = useMemo(() => {
        if (primaryWallet?.address) {
            return primaryWallet.address
        }
        return myAbstractAccount
    }, [primaryWallet?.address, myAbstractAccount])

    const { data: balance } = useBalance({
        address: walletToTransactWith,
        enabled: !!walletToTransactWith,
    })

    const { data: usdcBalance } = useUSDCBalance({
        address: walletToTransactWith,
        enabled: !!walletToTransactWith && enableUsdc,
    })

    const spaceAddress = spaceId ? SpaceAddressFromSpaceId(spaceId) : undefined

    const enableEOAUSDCAllowance = enableUsdc && primaryWallet?.type === 'eoa'

    const { data: eoaUSDCAllowance, isLoading: isLoadingEoaUSDCAllowance } = useUSDCAllowance({
        walletAddress: walletToTransactWith,
        spenderAddress: spaceAddress,
        enabled: enableEOAUSDCAllowance,
    })

    const effectiveEthMode = IS_ETH_MODE || isFallbackMode
    const loading =
        (isLoadingPrice && !isFallbackMode && !enableUsdc) ||
        (!price && !effectiveEthMode && !enableUsdc) ||
        isLoadingTokenId ||
        (enableEOAUSDCAllowance && isLoadingEoaUSDCAllowance)

    const tokenAmount = useMemo(() => {
        if (enableUsdc) {
            // USDC mode: convert cents directly to USDC (6 decimals)
            return tipValue ? calculateUsdcAmountFromCents(tipValue.amountInCents) : undefined
        } else {
            // ETH mode: existing logic
            return effectiveEthMode
                ? tipValue?.ethAmount
                    ? {
                          value: BigInt(Math.round(tipValue.ethAmount * 1e18)),
                          formatted: tipValue.ethAmount.toString(),
                      }
                    : undefined
                : tipValue && price
                  ? calculateEthAmountFromUsd({
                        cents: tipValue.amountInCents,
                        ethPriceInUsd: price,
                    })
                  : undefined
        }
    }, [enableUsdc, tipValue, effectiveEthMode, price])

    const { tip } = useTipTransaction()

    const { data: spaceInfo } = useContractSpaceInfoWithoutClient(spaceId)

    const _onTip = async (getSigner: GetSigner) => {
        const signer = await getSigner()
        if (!signer) {
            createPrivyNotAuthenticatedNotification()
            return
        }
        if (
            !messageOwnerAbstractAccount ||
            (!spaceId && !isDm) ||
            !channelId ||
            !tipValue ||
            !tokenAmount ||
            !walletToTransactWith
        ) {
            console.error('Missing required data for tip transaction', {
                messageOwnerAbstractAccount,
                spaceId,
                channelId,
                tipValue,
                tokenAmount,
            })
            popupToast(({ toast }) => (
                <StandardToast.Error message="Cannot process tip" toast={toast} />
            ))
            return
        }

        if (!isDm) {
            if (!messageOwner.isBot && !tokenId) {
                popupToast(({ toast }) => (
                    <StandardToast.Error message="Cannot process tip" toast={toast} />
                ))
                return
            }
        }

        const currency = enableUsdc ? usdcAddress : ETH_ADDRESS

        const tipParams = (() => {
            if (isDm) {
                return {
                    type: 'any',
                    receiverUserId: messageOwner.userId,
                    receiverUsername: messageOwner.username,
                    receiverAddress:
                        messageOwner.isBot && messageOwner.appAddress
                            ? messageOwner.appAddress
                            : messageOwnerAbstractAccount!,
                    senderAddress: walletToTransactWith,
                    messageId: eventId,
                    channelId,
                    currency,
                    amount: tokenAmount.value,
                    signer,
                    walletType: primaryWallet?.type ?? 'smartAccount',
                } satisfies TipParams
            } else if (
                messageOwner.isBot &&
                messageOwner.appAddress &&
                messageOwner.appId &&
                spaceId
            ) {
                return {
                    type: 'bot',
                    spaceId,
                    appId: messageOwner.appId,
                    appAddress: messageOwner.appAddress,
                    receiverUserId: messageOwner.userId,
                    receiverUsername: messageOwner.username,
                    receiverAddress: messageOwner.appAddress,
                    senderAddress: walletToTransactWith,
                    messageId: eventId,
                    channelId,
                    currency,
                    amount: tokenAmount.value,
                    signer,
                    walletType: primaryWallet?.type ?? 'smartAccount',
                } satisfies TipParams
            } else {
                if (!spaceId || !tokenId) {
                    throw new Error('Missing required data for member tip: spaceId or tokenId')
                }
                return {
                    type: 'member',
                    spaceId,
                    tokenId,
                    receiverUserId: messageOwner.userId,
                    receiverUsername: messageOwner.username,
                    receiverAddress: messageOwnerAbstractAccount!,
                    senderAddress: walletToTransactWith,
                    messageId: eventId,
                    channelId,
                    currency,
                    amount: tokenAmount.value,
                    signer,
                    walletType: primaryWallet?.type ?? 'smartAccount',
                } satisfies TipParams
            }
        })()

        await tip(tipParams, {
            onSuccess: (tipEvent) => {
                const { gatedSpace, pricingModule } = getCachedSpaceDataForAnalytics()
                trackPostedTip({
                    cents: tipValue.amountInCents,
                    tipAmount: tokenAmount.formatted,
                    recipient: tipEvent.receiver,
                    spaceName: spaceInfo?.name ?? '',
                    spaceId: spaceId ?? '',
                    isGated: gatedSpace,
                    pricingModule,
                })
                onTip?.(tipParams)
            },
            onError: (error) => {
                console.error('[TipConfirm] Tip failed:', error)
                onTipError?.(error)
                const { gatedSpace, pricingModule } = getCachedSpaceDataForAnalytics()
                trackPostedTipFailed({
                    isGated: gatedSpace,
                    pricingModule,
                    recipient: tipParams.receiverAddress,
                    spaceName: spaceInfo?.name ?? '',
                    spaceId: spaceId ?? '',
                    tipAmount: tokenAmount.formatted,
                    cents: tipValue.amountInCents,
                })
            },
        })
    }

    if (!tipValue) {
        return null
    }

    const currentBalance = enableUsdc ? usdcBalance : balance
    const hasInsufficientBalance =
        currentBalance && tokenAmount && currentBalance.value < tokenAmount.value

    const eoaHasInsufficientAllowance =
        enableEOAUSDCAllowance &&
        eoaUSDCAllowance !== undefined &&
        tokenAmount?.value !== undefined &&
        eoaUSDCAllowance < tokenAmount.value

    return (
        <Stack gap="sm">
            {loading && !isErrorPrice && !isErrorTokenId ? (
                <Loading />
            ) : hasInsufficientBalance ? (
                <InsufficientTipBalance
                    setTipValue={setTipValue}
                    enableUsdc={enableUsdc}
                    onInsufficientBalance={onInsufficientBalance}
                />
            ) : eoaHasInsufficientAllowance ? (
                <InsufficientEOAAllowance amount={tokenAmount.value} />
            ) : (
                <Summary
                    tipValue={tipValue}
                    tokenAmount={tokenAmount}
                    isErrorPrice={isErrorPrice}
                    isBot={messageOwner.isBot ?? false}
                    isErrorTokenId={isErrorTokenId}
                    loading={loading}
                    messageOwner={messageOwner}
                    tipPending={tipPending}
                    setTipValue={setTipValue}
                    isFallbackMode={isFallbackMode}
                    effectiveEthMode={effectiveEthMode}
                    enableUsdc={enableUsdc}
                    onTip={_onTip}
                    onCancel={onCancel}
                />
            )}
        </Stack>
    )
}

function Loading() {
    return (
        <Box centerContent height="x8">
            <ButtonSpinner color="gray2" />
        </Box>
    )
}

function Summary(props: {
    tipValue: TipOption
    tokenAmount: { value: bigint; formatted: string } | undefined
    isErrorPrice: boolean
    isBot: boolean
    isErrorTokenId: boolean
    loading: boolean
    messageOwner: LookupUser
    onTip: (getSigner: GetSigner) => void
    setTipValue: (tipValue: TipOption | undefined) => void
    onCancel?: () => void
    tipPending: boolean
    isFallbackMode: boolean
    effectiveEthMode: boolean
    enableUsdc?: boolean
}) {
    const {
        tipValue,
        tokenAmount,
        isErrorPrice,
        isErrorTokenId,
        loading,
        messageOwner,
        isBot,
        onTip,
        setTipValue,
        onCancel,
        tipPending,
        isFallbackMode,
        effectiveEthMode,
        enableUsdc,
    } = props

    const [isSending, setIsSending] = useState(false)

    const handleTip = (getSigner: GetSigner) => {
        setIsSending(true)
        onTip(getSigner)
    }

    return (
        <>
            <Box gap="md">
                <Text strong textAlign="center">
                    Confirm Tip
                </Text>
                {isErrorPrice && !isFallbackMode ? (
                    <Text textAlign="center" color="negative" paddingBottom="sm">
                        Failed to fetch ETH price
                    </Text>
                ) : isErrorTokenId && !isBot ? (
                    <Text textAlign="center" color="negative" paddingBottom="sm">
                        {`Failed to fetch token id for ${messageOwner.username}`}
                    </Text>
                ) : loading ? (
                    <Loading />
                ) : (
                    <>
                        {isFallbackMode && !tipValue.amountInCents && (
                            <Text size="xs" color="accent" textAlign="center" paddingBottom="sm">
                                Using ETH amounts due to price service unavailability
                            </Text>
                        )}
                        <Box paddingBottom="sm">
                            <Text textAlign="center" color="gray2" size="sm">
                                {enableUsdc ? (
                                    <>
                                        Send {tipValue.label.replace('$', '')} USDC to @
                                        {messageOwner.username}?
                                    </>
                                ) : effectiveEthMode ? (
                                    <>
                                        {tipValue.label}
                                        {!isFallbackMode && tipValue.amountInCents > 0 && (
                                            <> (${(tipValue.amountInCents / 100).toFixed(2)} USD)</>
                                        )}{' '}
                                        will be sent to @{messageOwner.username}?
                                    </>
                                ) : (
                                    <>
                                        {tipValue.label} ({tokenAmount?.formatted} ETH) will be sent
                                        to @{messageOwner.username}?
                                    </>
                                )}
                            </Text>
                        </Box>
                    </>
                )}
            </Box>

            <WalletReady withPrimaryWallet ConnectButton={<ConnectWalletButton />}>
                {({ getSigner }) => (
                    <Button
                        tone="cta1"
                        rounded="md"
                        size="button_sm"
                        disabled={loading || tipPending || isSending}
                        onClick={() => handleTip(getSigner)}
                    >
                        {isSending ? 'Sending...' : 'Send'}
                    </Button>
                )}
            </WalletReady>
            <Button
                color="cta1"
                rounded="md"
                size="button_sm"
                disabled={tipPending || isSending}
                onClick={() => {
                    setTipValue(undefined)
                    onCancel?.()
                }}
            >
                Cancel
            </Button>
        </>
    )
}

function ConnectWalletButton() {
    const userId = useMyUserId()
    const { data: primaryWallet } = usePrimaryWallet({ rootWallet: userId })

    return (
        <Stack centerContent gap="sm">
            <Text size="xs" color="gray2">
                Connect {shortAddress(primaryWallet?.address ?? '')} to send a tip.
            </Text>
            <PrimaryWalletNotConnectedButton
                buttonSize="button_sm"
                withTooltip={false}
                buttonRounded="md"
            />
        </Stack>
    )
}
